import { Link } from "react-router-dom";
import type { FinishedJob } from "../api/client";
import { chicagoTime, formatDuration } from "../lib/format";
import { DetailLine, ExpandableRow, PanelTail } from "./PanelTail.js";

/**
 * One finished job, rendered the same way wherever it appears: the Ops tail is the head
 * of the history page, and one record never has two renderings that can drift.
 *
 * **What the row says, and where.** The summary carries what can be scanned: when, what
 * kind of work, the ticker, the situation, and what the run produced. The opened row
 * carries what has to be read: the company's full name, the subject, the duration, the
 * attempt count and, on a failure, the error in full.
 */
export function FinishedJobRow({
  job,
  open,
  onToggle,
  /** The history page already sits under /ops; the tail links out to it. */
  linkSituations = true,
}: {
  job: FinishedJob;
  open: boolean;
  onToggle: () => void;
  linkSituations?: boolean;
}) {
  const at = chicagoTime(job.finishedAt);

  return (
    <ExpandableRow
      open={open}
      onToggle={onToggle}
      summary={
        <>
        <span className="fw" title={at?.full ?? job.finishedAt}>
          {at?.short ?? "—"}
        </span>
        <span className="ft">{job.kind}</span>
        {/*
          The ticker, where there is one, in front of the subject rather than replacing
          it: it makes a row recognisable and an accession does not.
        */}
        {job.ticker && <span className="fk">{job.ticker}</span>}
        <span className="fd">{job.subject ?? "no subject"}</span>
        {/*
          Which situation moved: a kind and an accession name work, and a situation
          names a result.
        */}
        {job.situation && <span className="fsit">{job.situation.name}</span>}
        {job.state === "failed" ? (
          <span className="ff">
            failed after {job.attempts} {job.attempts === 1 ? "try" : "tries"}
          </span>
        ) : (
          /*
            What the run produced, in the handler's own words. Absent, the duration takes
            the slot.
          */
          <span className="fo">
            {job.outcome ??
              (job.durationSeconds === null
                ? "no summary recorded"
                : `took ${formatDuration(job.durationSeconds)}`)}
          </span>
        )}
        </>
      }
      detail={
        <>
          <DetailLine k="finished" v={at?.full ?? job.finishedAt} />
          {/*
            The full company name, which the summary has no room for: a ticker is short and
            a name is certain.
          */}
          <DetailLine k="company" v={job.company ?? "not a company's filing"} />
          <DetailLine k="ticker" v={job.ticker ?? "none listed"} />
          <DetailLine k="subject" v={job.subject ?? "none"} />
          <DetailLine
            k="situation"
            v={
              job.situation ? (
                <>
                  {linkSituations ? (
                    <Link to={`/situations/${job.situation.id}`}>{job.situation.name}</Link>
                  ) : (
                    job.situation.name
                  )}
                  {/*
                    One filing can sit on several situations, so naming one and staying
                    silent about the others would claim they do not exist.
                  */}
                  {job.situation.alsoOn > 0 && (
                    <span className="dlnote">
                      {" "}
                      · this filing is also on {job.situation.alsoOn} other situation
                      {job.situation.alsoOn === 1 ? "" : "s"}
                    </span>
                  )}
                </>
              ) : (
                "none — this run was not about a situation"
              )
            }
          />
          <DetailLine k="outcome" v={job.outcome ?? "no summary recorded for this run"} />
          <DetailLine
            k="duration"
            v={job.durationSeconds === null ? "not recorded" : formatDuration(job.durationSeconds)}
          />
          <DetailLine k="attempts" v={job.attempts} />
          {job.state === "failed" && (
            <DetailLine
              k="error"
              v={<code className="dlerr">{job.lastError ?? "no message recorded"}</code>}
            />
          )}
        </>
      }
    />
  );
}

/** The finished-job tail, capped by `PanelTail`, with a link to the whole history. */
export function FinishedJobTail({ jobs }: { jobs: FinishedJob[] }) {
  return (
    <PanelTail
      items={jobs}
      idOf={(j) => j.id}
      renderRow={(job, open, toggle) => (
        <FinishedJobRow job={job} open={open} onToggle={toggle} />
      )}
      href="/ops/history"
      linkLabel="All finished jobs"
    />
  );
}
